import { useContext, useEffect, useState } from 'react';
import type { FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import UserContext from 'app/common/UserContext';
import PrivateContent from 'app/common/PrivateContent';
import { RealtimeContext } from 'app/common/RealtimeProvider';
import Icon from 'app/components/Icon';
import warningIcon from 'app/icons/warning_empty.svg';
import eventsIcon from 'app/icons/schedule.svg';
import blueprintIcon from 'app/icons/blueprint.svg';
import InputNumber from 'app/components/Input/InputNumber';
import blacklistIcon from 'app/icons/blacklist.svg';
import { parseFloatOrUndefined } from 'app/utils/numbers';
import useDebounce from 'app/hooks/useDebounce';
import monitoringIcon from 'app/icons/monitoring.svg';
import trophyIcon from 'app/icons/trophy-2.svg';
import SettingsIcons from 'app/icons/settings_2.svg';
import GlobalContext from 'app/common/GlobalContext';
// import betlogIcon from 'app/icons/betlog.svg';

import RealtimeIndicator from './RealtimeIndicator';
import NavBarLink from './NavBarLink';
import UserDropdown from './UserDropdown';
import style from './style.scss';

interface Props {
  collapsed: boolean;
}

const SidebarMenu: FC<Props> = ({ collapsed }) => {
  const [t] = useTranslation();
  const { status: realTimeStatus } = useContext(RealtimeContext);
  const { currentUser, logout, updatePreferences } = useContext(UserContext);
  const { outrightsSeriesOpenRemindersCount } = useContext(GlobalContext);

  const [overroundValue, setOverroundValue] = useState(
    currentUser?.preferences.overround_value?.toString()
  );
  const debouncedOverroundValue = useDebounce(overroundValue, 500);

  useEffect(() => {
    if (currentUser?.preferences.overround_value?.toString() !== debouncedOverroundValue) {
      updatePreferences({
        overround_value: parseFloatOrUndefined(debouncedOverroundValue ?? '') ?? 0
      });
    }
  }, [debouncedOverroundValue]);

  useEffect(() => {
    if (currentUser?.preferences.overround_value?.toString() !== overroundValue) {
      setOverroundValue(currentUser?.preferences.overround_value?.toString());
    }
  }, [currentUser?.preferences.overround_value]);

  const containerClassName = classnames(style.container, {
    [style.container_collapsed]: collapsed
  });
  const overroundContainerClassName = classnames(style.container, {
    [style.overround_container_collapsed]: collapsed
  });
  const inputClassName = classnames(style.displayedOverroundInput, {
    [style.activeInput]: currentUser?.preferences.overround_value !== undefined
  });

  return (
    <>
      {realTimeStatus === 'close' && (
        <PrivateContent feature="REALTIME_PROVIDER">
          <div className={containerClassName}>
            <RealtimeIndicator status={realTimeStatus} collapsed={collapsed} />
          </div>
        </PrivateContent>
      )}
      {currentUser?.preferences.overround_display && (
        <PrivateContent feature="DISPLAY_OVERROUND">
          <div
            className={classnames(overroundContainerClassName, style.displayedOverroundContainer)}
          >
            {collapsed ? (
              <span className={style.displayedOverroundTextCollapsed}>
                {t('top_bar-overround_displayed_collapsed')}
              </span>
            ) : (
              <span className={style.displayedOverroundText}>
                {t('top_bar-overround_displayed')}
              </span>
            )}
            <InputNumber
              minValue={0}
              maxValue={999}
              loseFocusOnEnter
              incrementBy={1}
              className={inputClassName}
              value={overroundValue ?? ''}
              onChange={newOverroundValue => {
                setOverroundValue(newOverroundValue);
              }}
            />
          </div>
        </PrivateContent>
      )}
      <div className={containerClassName}>
        <PrivateContent feature="EVENTS_PAGE">
          <NavBarLink
            to="/"
            collapsed={collapsed}
            label={t('top_bar-schedule')}
            icon={<Icon href={eventsIcon} className={style.eventsIcon} />}
          />
        </PrivateContent>
        <PrivateContent feature="OUTRIGHTS">
          <NavBarLink
            to="/outrights"
            collapsed={collapsed}
            label={t('top_bar-outrights')}
            icon={<Icon href={trophyIcon} className={style.eventsIcon} />}
            content={
              outrightsSeriesOpenRemindersCount > 0 ? (
                <div className={style.outrightsNotification}>
                  {outrightsSeriesOpenRemindersCount}
                </div>
              ) : null
            }
          />
        </PrivateContent>
        <PrivateContent feature="MONITORING">
          <NavBarLink
            to="/monitoring"
            collapsed={collapsed}
            label={t('top_bar-monitoring')}
            icon={<Icon href={monitoringIcon} className={style.monitoringIcon} />}
          />
        </PrivateContent>
        <PrivateContent feature="BLUEPRINTS">
          <NavBarLink
            to="/blueprints"
            collapsed={collapsed}
            label={t('top_bar-blueprints')}
            icon={<Icon href={blueprintIcon} className={style.blueprintIcon} />}
          />
        </PrivateContent>
        <PrivateContent feature="LADDER">
          <NavBarLink
            to="/odds-ladders"
            collapsed={collapsed}
            label={t('top_bar-ladders')}
            icon={<Icon href={SettingsIcons} className={style.settingsIcon} />}
          />
        </PrivateContent>
      </div>
      {/* <PrivateContent feature="BETLOG">
        <div className={containerClassName}>
          <NavBarLink
            to="/risk/tickets"
            collapsed={collapsed}
            label={t('top_bar-betlog')}
            icon={<Icon href={betlogIcon} className={style.betlogIcon} />}
          />
        </div>
      </PrivateContent> */}
      <div className={containerClassName}>
        <PrivateContent feature="INCIDENTS">
          <NavBarLink
            to="/incidents"
            collapsed={collapsed}
            label={t('top_bar-incidents')}
            icon={<Icon href={warningIcon} className={style.warningIcon} />}
          />
        </PrivateContent>
        <PrivateContent feature="BLACKLIST">
          <NavBarLink
            to="/blacklist"
            collapsed={collapsed}
            label={t('top_bar-blacklist')}
            icon={<Icon href={blacklistIcon} className={style.warningIcon} />}
          />
        </PrivateContent>
        {currentUser && (
          <UserDropdown
            collapsed={collapsed}
            user={currentUser}
            onLogout={logout}
            updatePreferences={updatePreferences}
          />
        )}
      </div>
    </>
  );
};

SidebarMenu.displayName = 'SidebarMenu';

export default SidebarMenu;
